import React, { useState, useMemo } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import { useAppSelector } from '../../../state/store';
import { getLocationsById, getSensorsById } from '../../../state/selectors';
import { sensorTypeDetails } from '../../../utils/sensorProperties';
import SensorIcon from '../../../styles/icons/SensorIcon';
import { themeProps } from '../../../styles/theme';
import useGlobalStyles from '../../../styles';
import useStyles from '../../../styles/tags';

interface TaggedSensorsListProps {
  taggedSensors: string[];
  existingTaggedSensors: string[];
  handleTagging: (value: string) => void;
}

function TaggedSensorsList({
  taggedSensors,
  existingTaggedSensors,
  handleTagging,
}: TaggedSensorsListProps) {
  const globalClasses = useGlobalStyles();
  const classes = useStyles();
  const sensorsById = useAppSelector(getSensorsById);
  const locationsById = useAppSelector(getLocationsById);
  const [hoveredId, setHoveredId] = useState('');

  // combine all old grouped and new grouped sensors without repeating
  const allSensors = useMemo(
    () => [
      ...existingTaggedSensors,
      ...taggedSensors.filter((item) => !existingTaggedSensors.includes(item)),
    ],
    [existingTaggedSensors, taggedSensors]
  );

  const sortedSensors = useMemo(
    () =>
      allSensors.sort((a, b) => {
        const nameA = sensorsById.get(a)?.name || a;
        const nameB = sensorsById.get(b)?.name || b;
        return nameA.localeCompare(nameB);
      }),
    [allSensors, sensorsById]
  );

  const getColor = (sensorId: string) => {
    let color = themeProps.colors.cobalt;
    const isRemoved = existingTaggedSensors.includes(sensorId) && !taggedSensors.includes(sensorId);
    const isAdded = !existingTaggedSensors.includes(sensorId) && taggedSensors.includes(sensorId);
    if (isRemoved) color = themeProps.colors.warningRed;
    else if (isAdded) color = themeProps.colors.happyGreen;
    return color;
  };

  const getPillLabel = (sensorId: string) => {
    let text = 'Click to remove';
    const isRemoved = existingTaggedSensors.includes(sensorId) && !taggedSensors.includes(sensorId);
    if (isRemoved) text = 'Click to add';
    return text;
  };

  return (
    <Box sx={{ marginTop: '16px' }}>
      <Typography variant="h6">Tagged Sensors</Typography>
      <List>
        {sortedSensors.map((sensorId) => {
          const sensorDetail = sensorsById.get(sensorId);
          const Icon = sensorDetail?.type
            ? sensorTypeDetails[sensorDetail.type]?.icon ?? SensorIcon
            : SensorIcon;

          return (
            <ListItem
              key={sensorId}
              className={globalClasses.compareListItem}
              sx={{ background: `${getColor(sensorId)}60 !important` }}
              onClick={() => handleTagging(sensorId)}
              onMouseEnter={() => setHoveredId(sensorId)}
              onMouseLeave={() => setHoveredId('')}
            >
              <Grid container sx={{ alignItems: 'center' }}>
                <Grid item sm={5} sx={{ display: 'inline-flex', alignItems: 'center' }}>
                  <Icon sx={{ color: getColor(sensorId) }} />
                  <Typography variant="body1" style={{ marginLeft: '5px' }}>
                    {sensorDetail?.name ?? sensorId}
                  </Typography>
                </Grid>
                <Grid item sm={3}>
                  {locationsById.get(sensorDetail?.location ?? '')?.name}
                </Grid>
                <Grid item sm={4}>
                  {hoveredId === sensorId && (
                    <span className={classes.tagPill}>{getPillLabel(sensorId)}</span>
                  )}
                </Grid>
              </Grid>
            </ListItem>
          );
        })}
      </List>
      {taggedSensors.length === 0 && (
        <Alert severity="error">No sensors Selected for grouping</Alert>
      )}
    </Box>
  );
}

export default TaggedSensorsList;
